import { useEffect, useRef, useState } from 'react';
import { apiClient } from '../utils/apiClient';

export const MIN_QUERY_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 300;

const EMPTY = { invoices: [], users: [], purchases: [], tickets: [] };

// The app-wide search (customers, tickets, invoices, users, purchases),
// shared by the top-bar dropdown, the command palette and the mobile search
// sheet so they always agree. Debounced; a slow earlier response can never
// overwrite a newer one.
export const useGlobalSearch = (query) => {
  const [searchResults, setSearchResults] = useState(EMPTY);
  const [customerResults, setCustomerResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const latest = useRef(0);

  useEffect(() => {
    const trimmed = String(query || '').trim();
    latest.current += 1;
    const ticket = latest.current;

    if (trimmed.length < MIN_QUERY_LENGTH) {
      setSearchResults(EMPTY);
      setCustomerResults([]);
      setIsSearching(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      // allSettled, not all: one failing search shouldn't blank the other.
      const [all, customers] = await Promise.allSettled([
        apiClient.post('/api/search/searchAll', { query: trimmed }),
        apiClient.get(`/api/search/customers?q=${encodeURIComponent(trimmed)}`)
      ]);
      if (ticket !== latest.current) return; // a newer query has taken over

      if (all.status === 'fulfilled') {
        const data = all.value || {};
        setSearchResults({
          invoices: data.invoices || [],
          users: data.users || [],
          purchases: data.purchases || [],
          tickets: data.tickets || []
        });
      } else {
        console.error('Error fetching search results:', all.reason);
        setSearchResults(EMPTY);
      }
      if (customers.status === 'fulfilled') {
        setCustomerResults((customers.value && customers.value.results) || []);
      } else {
        console.error('Error fetching customer search results:', customers.reason);
        setCustomerResults([]);
      }
      setIsSearching(false);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  return {
    searchResults,
    customerResults,
    isSearching,
    hasQuery: String(query || '').trim().length >= MIN_QUERY_LENGTH,
    hasAnyResults: customerResults.length > 0
      || searchResults.invoices.length > 0 || searchResults.users.length > 0
      || searchResults.purchases.length > 0 || searchResults.tickets.length > 0
  };
};
